import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, RefreshCw } from "lucide-react";

import { contractingKeys } from "@/lib/query-keys";

import { useCanAutoQualify } from "@/hooks/useCanAutoQualify";
import {
  describeOutdatedWorks,
  useQualificationAnalysis,
} from "@/hooks/useQualificationAnalysis";

/**
 * El cruce automático arriba del Descubrimiento (Comercial y Corporativo): muestra el avance
 * mientras corre y resume el resultado de la línea activa. En una línea sin marcar
 * no se pinta. El botón vive en Documentos.
 */
export function QualificationBanner({
  lineId,
  onShowQualified,
}: {
  lineId: number | null;
  /** Aplica el chip "Califico" al listado de abajo. */
  onShowQualified: () => void;
}) {
  const qc = useQueryClient();
  const canAutoQualify = useCanAutoQualify();
  const analysis = useQualificationAnalysis(lineId);
  // Sin los documentos esenciales no se cruza nada: ni el botón ni las obras nuevas.
  const stopped = analysis.missingEssentialDocuments.length > 0;
  const showsMoreThanCrossed =
    analysis.lines.find((line) => line.searchProfileId === lineId)?.onlyInterests === false;

  // Al terminar una corrida, las filas de abajo pasan de "Analizando…" a su veredicto.
  const wasAnalyzing = useRef(false);
  const isAnalyzing = analysis.status === "ANALYZING";
  useEffect(() => {
    if (wasAnalyzing.current && !isAnalyzing) {
      qc.invalidateQueries({ queryKey: contractingKeys.lists() });
    }
    wasAnalyzing.current = isAnalyzing;
  }, [isAnalyzing, qc]);

  if (!canAutoQualify) return null;
  if (analysis.status !== "INACTIVE" && !analysis.isLineMarked) return null;

  // Sin líneas marcadas no hay nada que contar: la invitación a cruzar ya no es un plan.
  if (analysis.status === "INACTIVE") return null;

  if (analysis.status === "ANALYZING") {
    const percent = Math.round((analysis.progressDone / analysis.progressTotal) * 100);
    return (
      <div className="mb-6 rounded-2xl bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="flex items-center gap-2 text-[15px] font-bold">
            <Loader2 className="size-4 animate-spin text-primary" />
            Analizando tus obras · {analysis.progressDone} de {analysis.progressTotal}
          </p>
          <span className="text-xs tabular-nums text-muted-foreground">{percent}%</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
          <div
            className="h-full bg-primary transition-[width] duration-300"
            style={{ width: `${percent}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Primero las que cierran registro antes.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-2xl bg-card p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <CheckCircle2 className="size-8 shrink-0 text-emerald-600" />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold text-slate-900 dark:text-slate-100">
            Calificas en {analysis.qualifiedWorks} de {analysis.totalWorks} obras con
            el registro abierto en esta línea
          </p>
          <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
            {stopped
              ? `Análisis detenido: falta ${analysis.missingEssentialDocuments.join(" y ")} en tus documentos.`
              : showsMoreThanCrossed
                ? "Solo analizamos las obras que coinciden con tus ítems; las demás de la lista no. Las nuevas se analizan solas apenas leemos sus bases."
                : "Las obras nuevas se analizan solas apenas leemos sus bases."}
          </p>
        </div>
        {analysis.qualifiedWorks > 0 && (
          <button
            type="button"
            onClick={onShowQualified}
            className="shrink-0 cursor-pointer text-sm font-bold text-primary hover:underline"
          >
            Ver dónde califico →
          </button>
        )}
      </div>
      {(stopped || analysis.outdatedWorks > 0) && (
        <div className="mt-4 flex flex-col gap-2 rounded-xl bg-slate-50 px-4 py-3 sm:flex-row sm:items-center dark:bg-slate-800/40">
          <RefreshCw className="size-4 shrink-0 text-slate-500" />
          <p className="min-w-0 flex-1 text-xs font-medium text-slate-700 dark:text-slate-200">
            {stopped
              ? "Súbelos y seguimos analizando tus obras."
              : `${describeOutdatedWorks(analysis)}.`}
          </p>
          <Link
            to="/settings/documents"
            className="shrink-0 cursor-pointer text-xs font-bold text-primary hover:underline"
          >
            {stopped ? "Subir documentos →" : "Actualizar →"}
          </Link>
        </div>
      )}
    </div>
  );
}
